<template>
  <section class="quote-section quote-line-items">
    <div class="line-items-header">
      <h3>Line Items</h3>
    </div>
    <div class="line-items-table-wrap">
      <table class="full-width-table line-items-table">
        <thead>
          <tr>
            <th class="type-cell">Type</th>
            <th class="item-cell">Item #</th>
            <th class="qty-cell">Qty</th>
            <th class="unit-cell">U/M</th>
            <th class="price-cell">Price</th>
            <th class="discount-cell">Discount %</th>
            <th class="amount-cell">Amount</th>
            <th class="actions-cell"></th>
          </tr>
        </thead>
        <tbody ref="lineBody">
          <QuoteLineItemRow
            v-for="(item, index) in quote.items"
            :key="item.ITEMID || item.CLIENTID"
            :item="item"
            :products="quoteProducts"
            :pricingLevelId="pricingLevelId"
            :isBlank="isBlankRow(item, index)"
            :isDragOver="dragOverIndex === index"
            @lineChanged="handleLineChanged"
            @delete="deleteLine(index)"
            @dragStart="startDrag(index, $event)"
            @dragOver="dragOver(index)"
            @drop="dropLine(index)"
            @dragEnd="endDrag" />
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import QuoteLineItemRow from './QuoteLineItemRow.vue';
import useQuoteProductsQuery from '@/composables/useQuoteProductsQuery';
import { ensureTrailingBlankLine, isLineStarted } from '@/composables/useQuoteLineItems';

const RETAIL_PRICING_LEVEL_ID = '9354b9f5-572a-4665-ae4d-c9ce0e78aa70';
const CONTRACTOR_PRICING_LEVEL_ID = 'c0174ae6-17c3-4ab8-a0ea-2dc8897815e5';

export default {
  name: 'QuoteLineItems',
  components: {
    QuoteLineItemRow,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
    selectedIndividual: {
      type: Object,
      default: null,
    },
  },
  setup() {
    useQuoteProductsQuery.useQuoteProductsQuery();

    return {
      quoteProducts: useQuoteProductsQuery.quoteProducts,
    };
  },
  data() {
    return {
      draggingIndex: null,
      dragOverIndex: null,
    };
  },
  computed: {
    pricingLevelId() {
      if (!this.quote.COMPANYID && this.quote.CONTACTID) {
        return this.selectedIndividual?.PRICINGLEVELID || this.quote.contact?.PRICINGLEVELID || RETAIL_PRICING_LEVEL_ID;
      }
      return this.selectedCompany?.PRICINGLEVELID || this.quote.company?.PRICINGLEVELID || CONTRACTOR_PRICING_LEVEL_ID;
    },
  },
  watch: {
    'quote.items': {
      handler() {
        this.ensureLines();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    isBlankRow(item, index) {
      return index === this.quote.items.length - 1 && !isLineStarted(item);
    },
    ensureLines() {
      if (!Array.isArray(this.quote.items)) {
        this.quote.items = [];
      }
      ensureTrailingBlankLine(this.quote.items);
      this.assignSortOrder();
    },
    assignSortOrder() {
      this.quote.items.forEach((item, index) => {
        item.SORTORDER = index;
      });
    },
    handleLineChanged() {
      this.$nextTick(() => this.ensureLines());
    },
    deleteLine(index) {
      this.quote.items.splice(index, 1);
      this.ensureLines();
    },
    startDrag(index, event) {
      this.draggingIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    },
    dragOver(index) {
      if (this.draggingIndex === null) return;
      this.dragOverIndex = index;
    },
    dropLine(index) {
      if (this.draggingIndex === null || this.draggingIndex === index) {
        this.endDrag();
        return;
      }

      const dropOnBlank = this.isBlankRow(this.quote.items[index], index);
      const [moved] = this.quote.items.splice(this.draggingIndex, 1);
      const insertIndex = dropOnBlank ? this.quote.items.length - 1 : this.draggingIndex < index ? index - 1 : index;
      this.quote.items.splice(insertIndex, 0, moved);
      this.ensureLines();
      this.endDrag();
    },
    endDrag() {
      this.draggingIndex = null;
      this.dragOverIndex = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-line-items {
  .line-items-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 1.5rem;
  }

  h3 {
    margin: 0;
    color: $textColor87;
  }

  .line-items-table-wrap {
    overflow: visible;
  }

  .line-items-table {
    min-width: 36rem;
    width: 100%;
    overflow: visible;

    :deep(th) {
      // Match input text: cell padding + input border + input padding
      padding-left: calc(0.5rem + 1px + 0.35rem);
    }

    :deep(.table-input),
    :deep(.table-select) {
      padding-left: 0.5rem;
    }

    :deep(th:first-child) {
      // First column has no cell padding — only match select border + padding
      padding-left: calc(1px + 0.35rem);
    }

    :deep(td:first-child) {
      padding-left: 0;
    }

    :deep(th:last-child),
    :deep(td:last-child) {
      padding-right: 0;
    }

    :deep(.type-cell) {
      width: 7rem;
    }

    :deep(tbody),
    :deep(tr),
    :deep(.item-cell) {
      overflow: visible;
    }

    :deep(tr:focus-within) {
      position: relative;
      z-index: 1000;
    }

    :deep(.qty-cell) {
      width: 4rem;
    }

    :deep(.unit-cell) {
      width: 5.5rem;
    }

    :deep(.price-cell),
    :deep(.discount-cell),
    :deep(.amount-cell) {
      width: 6rem;
    }

    :deep(.actions-cell) {
      width: 3.75rem;
      padding: 0;
      padding-left: 0.25rem;
      text-align: center;
      vertical-align: middle;
    }

    @media (min-width: 75rem) {
      :deep(.qty-cell) {
        width: 5rem;
      }

      :deep(.unit-cell) {
        width: 6.5rem;
      }

      :deep(.price-cell),
      :deep(.discount-cell),
      :deep(.amount-cell) {
        width: 7.25rem;
      }
    }

    :deep(td) {
      vertical-align: middle;
    }

    :deep(.table-input input),
    :deep(.table-input textarea),
    :deep(.table-select select) {
      padding: 0.4rem 0.35rem;
      border-color: transparent;

      &:hover {
        border-color: $inputBorderColor;
      }
    }

    :deep(.quote-product-search input) {
      padding-right: 1.8rem;
    }

    :deep(.table-select select) {
      background-image: none;

      &:hover {
        background-image: url('../../assets/dropdown-icon.svg');
      }
    }

    :deep(tr:focus-within .table-input input),
    :deep(tr:focus-within .table-input textarea),
    :deep(tr:focus-within .table-select select),
    :deep(tr.blank-row .table-input input),
    :deep(tr.blank-row .table-input textarea),
    :deep(tr.blank-row .table-select select) {
      border-color: $inputBorderColor;
    }

    :deep(tr:focus-within .table-select select),
    :deep(tr.blank-row .table-select select) {
      background-image: url('../../assets/dropdown-icon.svg');
    }

    :deep(tr:focus-within .search-icon),
    :deep(tr.blank-row .search-icon) {
      opacity: 0.6;
    }

    :deep(.amount-cell) {
      color: $textColor87;
      text-align: left;
      padding-left: calc(0.5rem + 1px + 0.35rem);
    }

    :deep(th.amount-cell) {
      text-align: left;
    }

    :deep(.comment-cell) {
      padding-top: 0.35rem;
      padding-bottom: 0.35rem;
    }

    :deep(.comment-cell textarea) {
      display: block;
      width: 100%;
      min-height: 2.75rem;
      resize: vertical;
      border: 1px solid transparent;
      border-radius: 0.25rem;
      padding: 0.45rem 0.5rem;
      font: inherit;
      color: $textColor87;
      background: transparent;
      box-sizing: border-box;

      &:hover {
        border-color: $inputBorderColor;
      }
    }

    :deep(tr:focus-within .comment-cell textarea),
    :deep(tr.blank-row .comment-cell textarea) {
      border-color: $inputBorderColor;
    }

    :deep(.comment-row td) {
      vertical-align: top;
    }

    :deep(.comment-row .type-cell),
    :deep(.comment-row .actions-cell) {
      padding-top: 0.55rem;
    }

    :deep(.line-actions) {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.55rem;
      height: 100%;
      min-height: 2.25rem;
    }

    :deep(.actions-cell button) {
      border: none;
      background: transparent;
      padding: 0;
      margin: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      line-height: 0;
    }

    :deep(.delete-btn) {
      img {
        width: 1.25rem;
        height: 1.25rem;
      }

      img#noHover {
        display: block;
      }

      img#hover {
        display: none;
      }

      &:hover {
        img#noHover {
          display: none;
        }

        img#hover {
          display: block;
        }
      }
    }

    :deep(.drag-handle) {
      flex-direction: column;
      gap: 0.18rem;
      width: 1.15rem;
      height: 1.25rem;
      cursor: grab;
    }

    :deep(.drag-handle span) {
      display: block;
      width: 0.95rem;
      height: 0.13rem;
      background: $textColor60;
      border-radius: 0.1rem;
    }

    :deep(tr.drag-over td),
    :deep(tr.blank-row.drag-over-bottom td) {
      background: transparent;
      box-shadow: inset 0 2px 0 rgba($alertBlueColor, 0.65);
    }
  }
}
</style>
